import { useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import droplet from '../../assets/droplet.png';
import './dropActiveList.scss';
import { claimDailyReward, claimFollowReward, getMemberActivities, type MemberActivityItem } from '../../api/memberActivities';
import { useDispatch } from 'react-redux';
import { setSnackbarOpen } from '../../store/taskSlice';
import { fetchMemberInfoAsync, setGlobalDialogTips, setPendingTasks } from "../../store/memberSlice";
import {AppDispatch} from "../../store";
import {memberInfoProps, PendingTask, PendingTaskType} from '../../types/member.type';
import {useMatomoTrackingPoint} from "../../hooks/useMatomoTrackingPoint";
import { initUserConfig } from '../../config/userConfig';
import {usePendingTask, usePendingTaskPoller} from "../../hooks/usePendingTask";
import {getActivityVariant} from "../../util/activityAbTest";

function renderDescWithHighlight(description: string | null, rewardAmount: number) {
  if (!description) return null;
  const amountStr = String(rewardAmount ?? '');
  if (!amountStr) return description;
  if (!description.includes(amountStr)) return description;

  const parts = description.split(amountStr);
  if (parts.length < 2) return description;

  return (
      <>
        {parts[0]}
        <span style={{ color: 'var(--c-brand)' }}>&nbsp;{amountStr}</span>
        {parts.slice(1).join(amountStr)}
      </>
  );
}

export default function DropActiveList(props: {handleSetLabelDialogControl: ()=>void; memberInfo?: memberInfoProps | null; boardId?: string | null}) {
  const [activities, setActivities] = useState<MemberActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dailyDropLoading, setDailyDropLoading] = useState(false);
  const [followRewardLoading, setFollowRewardLoading] = useState(false);
  const [activityDialogUrl, setActivityDialogUrl] = useState<string | null>(null);

  const {trackRewardGetClick, trackInvitePopupClick, trackJoinQqgroupClick, trackFollowAccountClick} = useMatomoTrackingPoint()

  const dispatch = useDispatch<AppDispatch>();
  const { handleSetLabelDialogControl, memberInfo, boardId } = props;
  const isMobile = useMediaQuery('(max-width:768px)');

  const { startPolling } = usePendingTaskPoller(() => {
    dispatch(fetchMemberInfoAsync());
  });

  const hasDailyLoginReward = usePendingTask(PendingTaskType.ACTIVITY_REWARD_CLAIM);
  const hasFollowRewardPending = usePendingTask(PendingTaskType.CLAIM_FOLLOW_REWARD);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await getMemberActivities();
        if (!cancelled && res?.success && Array.isArray(res.data)) {
          setActivities(res.data);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const cards = useMemo(() => {
    const statusOrder: Record<string, number> = {
      claimable: 0,
      pending: 1,
      claimed: 2,
    };

    const sortedActivities = [...activities].sort((a, b) => {
      const statusA = memberInfo?.activities?.find(
          (x) => String(x.activityId) === String(a.activityId),
      )?.status;
      const statusB = memberInfo?.activities?.find(
          (x) => String(x.activityId) === String(b.activityId),
      )?.status;

      const orderA = statusOrder[statusA || 'pending'] ?? statusOrder.pending;
      const orderB = statusOrder[statusB || 'pending'] ?? statusOrder.pending;

      return orderA - orderB;
    });

    return sortedActivities.map((a) => {
      const activityStatus = memberInfo?.activities?.find(
          (x) => String(x.activityId) === String(a.activityId),
      )?.status;

      const isClaimed = activityStatus === 'claimed';
      const isClaimable = activityStatus === 'claimable';
      const isGettingDailyDrops = a.redirectLink === 'get_daily_drops' && (!!hasDailyLoginReward || dailyDropLoading);
      const isGettingFollowReward = a.redirectLink === 'FOCUS_SUBMISSION_PAGE' && (!!hasFollowRewardPending || followRewardLoading);

      const buttonLabel = isGettingDailyDrops || isGettingFollowReward ? '领取中' : (isClaimed ? '已领取' : (isClaimable ? '立即领取' : '去完成'));
      const buttonVariant = isGettingDailyDrops || isGettingFollowReward ? 'muted' : (isClaimed ? 'muted' : (isClaimable ? 'primary' : 'outline'));

      return {
        id: a.activityId,
        title: a.name,
        desc: a.description,
        rewardAmount: a.rewardAmount,
        redirectLink: a.redirectLink,
        buttonLabel,
        buttonVariant,
        disabled: !!isGettingDailyDrops || !!isGettingFollowReward || isClaimed,
        itemClaimed: isClaimed,
        showLimitedBadge: a.taskType === 'completable',
      };
    });
  }, [activities, memberInfo, dailyDropLoading, followRewardLoading, hasFollowRewardPending]);

  const handleCardClick = async (redirectLink?: string, activityId?: MemberActivityItem['activityId']) => {
    const activityStatus = memberInfo?.activities?.find(
        (x) => activityId !== undefined && String(x.activityId) === String(activityId),
    )?.status;

    if (activityStatus === 'claimed') return;

    if (redirectLink === 'FOCUS_SUBMISSION_PAGE') {
      // 可领取状态：直接调用领取接口（参考每日水滴）
      if (activityStatus === 'claimable') {
        if (activityId === undefined) return;
        setFollowRewardLoading(true);
        let res;
        try {
          res = await claimFollowReward(activityId);
        } finally {
          setFollowRewardLoading(false);
        }

        const page_id = window.location.href;
        trackRewardGetClick({page_id, select_content: res?.data?.activityName, login_status: res?.success})

        if (res?.success && res.data?.activityId !== undefined) {
          startPolling();
          dispatch(setPendingTasks([{ taskId: 'mock', taskType: PendingTaskType.CLAIM_FOLLOW_REWARD }]));
          await initUserConfig(dispatch);
          return;
        }

        dispatch(
            setGlobalDialogTips({
              type: 'error',
              title: '领取失败',
              content: res?.message || '领取失败，请重试。',
              buttonContent: '确认',
            })
        );
        return;
      }

      // 非可领取（如待审核）：AB 测试按 userId/deviceId 稳定分流到截图版或填id版活动页
      const variant = getActivityVariant();
      // 点击「去完成」：上报关注官号点击埋点
      trackFollowAccountClick({page_id: window.location.href, select_content: variant});
      const url = `${process.env.PUBLIC_URL || ''}/${variant}`;
      if (isMobile) {
        // 移动端在当前页全屏 Dialog 内打开，可关闭
        setActivityDialogUrl(url);
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      return;
    }
    if (redirectLink === 'qq_group_join_link') {
      const page_id = window.location.href;
      trackJoinQqgroupClick({
        page_id,
        board_id: boardId ?? ''
      });
      window.open('https://qm.qq.com/q/DdeNxVg01i', '_blank', 'noopener,noreferrer');
      return;
    }
    if (redirectLink === 'invitation_new_users_popup') {
      trackInvitePopupClick({invite_enter_from: boardId ? 'board' : 'home_page'})
      handleSetLabelDialogControl()
    }
    if (redirectLink === 'get_daily_drops') {
      if (activityStatus !== 'claimable' || activityId === undefined) return;
      setDailyDropLoading(true);
      let res;
      try {
        res = await claimDailyReward(activityId);
      } finally {
        setDailyDropLoading(false);
      }

      const page_id = window.location.href;
      trackRewardGetClick({page_id, select_content: res?.data?.activityName, login_status: res?.success})

      if (res?.success && res.data?.activityId !== undefined) {
        startPolling()
        dispatch(setPendingTasks([{ taskId: 'mock', taskType: PendingTaskType.ACTIVITY_REWARD_CLAIM }]));
        await initUserConfig(dispatch);
        return;
      }

      dispatch(
          setGlobalDialogTips({
            type: 'error',
            title: '领取失败',
            content: res?.message || '领取失败，请重试。',
            buttonContent: '确认',
          })
      );
    }
  };

  if (loading) {
    return <div className="dropWaterDetailMain dropActiveListMain" />;
  }

  return (
      <div className="dropWaterDetailMain dropActiveListMain">
        <div className="dropActiveList">
          {cards.map((c) => (
              <div
                  key={c.id}
                  className={[
                    'dropActiveList__item',
                    c.itemClaimed && 'dropActiveList__item--claimed',
                  ]
                      .filter(Boolean)
                      .join(' ')
                  }
              >
                <div className="dropActiveList__iconWrap" aria-hidden>
                  <img className="dropActiveList__icon" src={droplet} alt="" />
                </div>

                <div className="dropActiveList__body">
                  <div className="dropActiveList__titleRow">
                    <div className="dropActiveList__title">{c.title}</div>
                    {c.redirectLink === 'FOCUS_SUBMISSION_PAGE' && (
                        <div className="dropActiveList__badge dropActiveList__badge--new">新活动</div>
                    )}
                    <div className="dropActiveList__badge">限时活动</div>
                  </div>
                  <div className="dropActiveList__desc">
                    {renderDescWithHighlight(c.desc, c.rewardAmount)}
                  </div>
                </div>
                  <button
                      type="button"
                      className={[
                        'dropActiveList__button',
                        c.buttonVariant === 'primary' && 'dropActiveList__button--primary',
                        c.buttonVariant === 'outline' && 'dropActiveList__button--outline',
                        c.buttonVariant === 'muted' && 'dropActiveList__button--muted',
                      ]
                          .filter(Boolean)
                          .join(' ')}
                      disabled={c.disabled}
                      onClick={() => handleCardClick(c.redirectLink, c.id)}
                  >
                    {c.buttonLabel}
                  </button>
              </div>
          ))}
        </div>

        <Dialog
            open={!!activityDialogUrl}
            onClose={() => setActivityDialogUrl(null)}
            fullScreen
        >
          <div className="dropActiveList__dialogRoot">
            <div className="dropActiveList__dialogHeader">
              <span>关注领水滴活动</span>
              <button
                  type="button"
                  className="dropActiveList__dialogClose"
                  onClick={() => setActivityDialogUrl(null)}
              >
                ✕
              </button>
            </div>
            {activityDialogUrl && (
                <iframe
                    src={activityDialogUrl}
                    title="关注活动"
                    className="dropActiveList__dialogFrame"
                />
            )}
          </div>
        </Dialog>
      </div>
  );
}